import React, { useState } from 'react';
import FilterCheckboxList from '../filters/FilterCheckboxList';
import FilterDateRange from '../filters/FilterDateRange';
import FilterIcon from '../filters/FilterIcon';
import FilterSection from '../filters/FilterSection';
import { FilterOptionCounts, withOptionCounts } from '../filters/counts';
import { formatDateRangeLabel } from '../filters/date';
import { getGroupSelectionSummary, getSelectionSummary } from '../filters/selection';
import { DashboardFilterDateRange, DashboardFilterOption, DashboardFilterViewMode } from './types';

type SectionKey =
  | 'viewMode'
  | 'timescale'
  | 'contributingOrganisations'
  | 'search'
  | 'observation'
  | 'projects'
  | 'locations'
  | 'monitoring'
  | 'monitoringGroups'
  | 'monitoringLocations';

const DEFAULT_EXPANDED: Record<SectionKey, boolean> = {
  viewMode: false,
  timescale: false,
  contributingOrganisations: false,
  search: false,
  observation: false,
  projects: false,
  locations: false,
  monitoring: false,
  monitoringGroups: false,
  monitoringLocations: false,
};

export interface DashboardFilterPanelProps {
  viewMode: string;
  viewModeOptions: DashboardFilterViewMode[];
  fromDate: Date | null;
  toDate: Date | null;
  contributingOrganisations: DashboardFilterOption[];
  selectedContributingOrganisations: string[];
  projects: DashboardFilterOption[];
  selectedProjects: string[];
  locations: DashboardFilterOption[];
  selectedLocations: string[];
  monitoringGroups: DashboardFilterOption[];
  selectedMonitoringGroups: string[];
  monitoringLocations: DashboardFilterOption[];
  selectedMonitoringLocations: string[];
  projectCounts: FilterOptionCounts;
  locationCounts: FilterOptionCounts;
  searchTerm: string;
  showTimescale: boolean;
  showSearch: boolean;
  showContributingOrganisations: boolean;
  showProjects: boolean;
  showLocations: boolean;
  showMonitoringGroups: boolean;
  showMonitoringLocations: boolean;
  showDownloadObservations: boolean;
  onViewModeChange: (mode: string) => void;
  onDateRangeChange: (range: DashboardFilterDateRange) => void;
  onContributingOrganisationsChange: (selected: string[]) => void;
  onProjectsChange: (selected: string[]) => void;
  onLocationsChange: (selected: string[]) => void;
  onMonitoringGroupsChange: (selected: string[]) => void;
  onMonitoringLocationsChange: (selected: string[]) => void;
  onSearchTermChange: (term: string) => void;
  onDownloadObservations: () => void;
}

const DashboardFilterPanel: React.FC<DashboardFilterPanelProps> = ({
  viewMode,
  viewModeOptions,
  fromDate,
  toDate,
  contributingOrganisations,
  selectedContributingOrganisations,
  projects,
  selectedProjects,
  locations,
  selectedLocations,
  monitoringGroups,
  selectedMonitoringGroups,
  monitoringLocations,
  selectedMonitoringLocations,
  projectCounts,
  locationCounts,
  searchTerm,
  showTimescale,
  showSearch,
  showContributingOrganisations,
  showProjects,
  showLocations,
  showMonitoringGroups,
  showMonitoringLocations,
  showDownloadObservations,
  onViewModeChange,
  onDateRangeChange,
  onContributingOrganisationsChange,
  onProjectsChange,
  onLocationsChange,
  onMonitoringGroupsChange,
  onMonitoringLocationsChange,
  onSearchTermChange,
  onDownloadObservations,
}) => {
  const [expanded, setExpanded] = useState<Record<SectionKey, boolean>>({ ...DEFAULT_EXPANDED });

  const toggle = (section: SectionKey) => {
    setExpanded((current) => ({ ...current, [section]: !current[section] }));
  };

  const collapse = (section: SectionKey) => {
    setExpanded((current) => ({ ...current, [section]: false }));
  };

  const currentViewMode = viewModeOptions.find((option) => option.key === viewMode)?.value || '';
  const timescaleSummary = fromDate && toDate ? formatDateRangeLabel(fromDate, toDate) : '';
  const searchSummary = searchTerm ? `“${searchTerm}”` : 'Search & download';
  const showObservationGroup = showProjects || showLocations;
  const showMonitoringGroup = showMonitoringGroups || showMonitoringLocations;
  const observationSummary = getGroupSelectionSummary([
    ...(showProjects ? [{ selected: selectedProjects, options: projects }] : []),
    ...(showLocations ? [{ selected: selectedLocations, options: locations }] : []),
  ]);
  const monitoringSummary = getGroupSelectionSummary([
    ...(showMonitoringGroups ? [{ selected: selectedMonitoringGroups, options: monitoringGroups }] : []),
    ...(showMonitoringLocations ? [{ selected: selectedMonitoringLocations, options: monitoringLocations }] : []),
  ]);

  return (
    <div className="stream-filter-panel">
      <FilterSection
        id="dashboard-view-mode"
        title="View Mode"
        icon="eye"
        expanded={expanded.viewMode}
        onToggle={() => toggle('viewMode')}
        summary={currentViewMode}
      >
        <div className="sf-radio-group" role="radiogroup" aria-label="View mode">
          {viewModeOptions.map((option) => {
            const selected = option.key === viewMode;
            return (
              <label className={`sf-radio ${selected ? 'is-selected' : ''}`} key={option.key}>
                <input
                  type="radio"
                  name="dashboard-view-mode"
                  checked={selected}
                  onChange={() => {
                    if (!selected) onViewModeChange(option.key);
                  }}
                />
                <span className="sf-radio__dot" aria-hidden="true" />
                <span>{option.value}</span>
              </label>
            );
          })}
        </div>
      </FilterSection>

      {showTimescale ? (
        <FilterSection
          id="dashboard-timescale"
          title="Timescale"
          icon="clock"
          expanded={expanded.timescale}
          onToggle={() => toggle('timescale')}
          summary={timescaleSummary}
          summaryIsDateRange
        >
          <FilterDateRange
            fromDate={fromDate}
            toDate={toDate}
            onChange={(rangeFrom, rangeTo) => onDateRangeChange({ fromDate: rangeFrom, toDate: rangeTo })}
            onDone={() => collapse('timescale')}
          />
        </FilterSection>
      ) : null}

      {showContributingOrganisations ? (
        <FilterSection
          id="dashboard-contributing-organisations"
          title="Contributing Organisation"
          icon="organisation"
          expanded={expanded.contributingOrganisations}
          onToggle={() => toggle('contributingOrganisations')}
          summary={getSelectionSummary(selectedContributingOrganisations, contributingOrganisations)}
        >
          <FilterCheckboxList
            options={contributingOrganisations}
            selected={selectedContributingOrganisations}
            onChange={onContributingOrganisationsChange}
            ariaLabel="Contributing organisations"
            emptyText="No contributing organisations available"
          />
        </FilterSection>
      ) : null}

      {showSearch ? (
        <FilterSection
          id="dashboard-search"
          title="Search in Observations"
          icon="search"
          expanded={expanded.search}
          onToggle={() => toggle('search')}
          summary={searchSummary}
          summaryMuted={!searchTerm}
        >
          <label className="sf-field-label" htmlFor="sf-dashboard-search">
            Search observations
          </label>
          <div className="sf-search-field">
            <span className="sf-search-field__icon" aria-hidden="true">
              <FilterIcon name="search" size={16} />
            </span>
            <input
              id="sf-dashboard-search"
              type="search"
              value={searchTerm}
              onChange={(event) => onSearchTermChange(event.target.value)}
              placeholder="Location, notes, values…"
              autoComplete="off"
            />
          </div>

          {showDownloadObservations ? (
            <button
              type="button"
              className="sf-button sf-button--secondary sf-button--full"
              onClick={onDownloadObservations}
            >
              <FilterIcon name="download" size={16} />
              Download Observations
            </button>
          ) : null}
        </FilterSection>
      ) : null}

      {showObservationGroup ? (
        <FilterSection
          id="dashboard-observations"
          title="Observation"
          icon="observations"
          expanded={expanded.observation}
          onToggle={() => toggle('observation')}
          summary={observationSummary}
          className="sf-section--group"
        >
          {showProjects ? (
            <FilterSection
              id="dashboard-projects"
              title="Project"
              icon="project"
              expanded={expanded.projects}
              onToggle={() => toggle('projects')}
              summary={getSelectionSummary(selectedProjects, projects)}
              className="sf-section--nested"
            >
              <FilterCheckboxList
                options={withOptionCounts(projects, projectCounts)}
                selected={selectedProjects}
                onChange={onProjectsChange}
                ariaLabel="Projects"
                emptyText="No projects available for this site"
              />
            </FilterSection>
          ) : null}

          {showLocations ? (
            <FilterSection
              id="dashboard-locations"
              title="Observation Location"
              icon="location"
              expanded={expanded.locations}
              onToggle={() => toggle('locations')}
              summary={getSelectionSummary(selectedLocations, locations)}
              className="sf-section--nested"
            >
              <FilterCheckboxList
                options={withOptionCounts(locations, locationCounts)}
                selected={selectedLocations}
                onChange={onLocationsChange}
                ariaLabel="Observation locations"
                emptyText="No observation locations available"
              />
            </FilterSection>
          ) : null}
        </FilterSection>
      ) : null}

      {showMonitoringGroup ? (
        <FilterSection
          id="dashboard-monitoring"
          title="Monitoring"
          icon="monitoring"
          expanded={expanded.monitoring}
          onToggle={() => toggle('monitoring')}
          summary={monitoringSummary}
          className="sf-section--group"
        >
          {showMonitoringGroups ? (
            <FilterSection
              id="dashboard-monitoring-groups"
              title="Monitoring Group"
              icon="layers"
              expanded={expanded.monitoringGroups}
              onToggle={() => toggle('monitoringGroups')}
              summary={getSelectionSummary(selectedMonitoringGroups, monitoringGroups)}
              className="sf-section--nested"
            >
              <FilterCheckboxList
                options={monitoringGroups}
                selected={selectedMonitoringGroups}
                onChange={onMonitoringGroupsChange}
                ariaLabel="Monitoring groups"
                emptyText="No monitoring groups available"
              />
            </FilterSection>
          ) : null}

          {showMonitoringLocations ? (
            <FilterSection
              id="dashboard-monitoring-locations"
              title="Monitoring Location"
              icon="station"
              expanded={expanded.monitoringLocations}
              onToggle={() => toggle('monitoringLocations')}
              summary={getSelectionSummary(selectedMonitoringLocations, monitoringLocations)}
              className="sf-section--nested"
            >
              <FilterCheckboxList
                options={monitoringLocations}
                selected={selectedMonitoringLocations}
                onChange={onMonitoringLocationsChange}
                ariaLabel="Monitoring locations"
                emptyText="No monitoring locations available"
              />
            </FilterSection>
          ) : null}
        </FilterSection>
      ) : null}
    </div>
  );
};

export default DashboardFilterPanel;
